import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export const useAttendeeData = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [favoriteSpeakers, setFavoriteSpeakers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recommendedEvents, setRecommendedEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);

  const loadData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setProfile(profileData);

      // Load registrations with event data
      const { data: regsData } = await supabase
        .from('event_registrations')
        .select('*, events(id, title, event, start_date, end_date, start, end, location, venue_name, venue, city, image_url, status, address, latitude, longitude, description, organization, org, event_type, is_paid_event, registration_fee, hotels, nearest_airport, airport, website_url)')
        .eq('attendee_email', user.email)
        .order('created_at', { ascending: false });

      setRegistrations(regsData || []);

      // Find next upcoming registered event
      if (regsData && regsData.length > 0) {
        const upcomingReg = regsData
          .filter(reg => reg.status !== 'cancelled' && new Date(reg.events?.start_date || reg.events?.start) >= new Date())
          .sort((a, b) => new Date(a.events?.start_date || a.events?.start) - new Date(b.events?.start_date || b.events?.start))[0];
        setNextEvent(upcomingReg?.events || null);
      }

      // Load saved events
      const { data: savedEventsData } = await supabase
        .from('user_saved_events')
        .select('event_id')
        .eq('user_id', user.id);

      let savedEventsList = [];
      if (savedEventsData && savedEventsData.length > 0) {
        const eventIds = savedEventsData.map(se => se.event_id);
        const { data: eventsData } = await supabase
          .from('events')
          .select('id, title, start_date, end_date, location, venue_name, city, image_url, status, description')
          .in('id', eventIds)
          .order('start_date');
        savedEventsList = eventsData || [];
      }
      setSavedEvents(savedEventsList);

      // Load favorite speakers
      const { data: favSpeakersData } = await supabase
        .from('user_favorite_speakers')
        .select('speaker_id')
        .eq('user_id', user.id);

      let favSpeakersList = [];
      if (favSpeakersData && favSpeakersData.length > 0) {
        const speakerIds = favSpeakersData.map(fs => fs.speaker_id);
        const { data: speakersData } = await supabase
          .from('speakers')
          .select('id, name, title, photo_url, church_name')
          .in('id', speakerIds);
        favSpeakersList = speakersData || [];
      }
      setFavoriteSpeakers(favSpeakersList);

      // Load upcoming events (general)
      const today = new Date().toISOString().split('T')[0];
      const { data: upcoming } = await supabase
        .from('events')
        .select('id, title, start_date, end_date, location, venue_name, city, image_url, description, status')
        .gte('start_date', today)
        .in('status', ['upcoming', 'ongoing'])
        .order('start_date')
        .limit(10);
      setUpcomingEvents(upcoming || []);

      // Load recommended events based on interests
      if (profileData?.interests?.length > 0) {
        const { data: recommended } = await supabase
          .from('events')
          .select('id, title, start_date, end_date, location, venue_name, city, image_url, description, status')
          .gte('start_date', today)
          .in('status', ['upcoming', 'ongoing'])
          .order('start_date')
          .limit(20);

        const filtered = (recommended || []).filter(event => {
          const eventText = `${event.title} ${event.description || ''}`.toLowerCase();
          return profileData.interests.some(interest =>
            eventText.includes(interest.toLowerCase())
          );
        });
        setRecommendedEvents(filtered.slice(0, 5));
      }

    } catch (err) {
      console.error('Error loading attendee data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalSpent = registrations
    .filter(r => r.status !== 'cancelled' && r.amount_paid)
    .reduce((sum, r) => sum + (r.amount_paid || 0), 0);

  const upcomingRegistrations = registrations
    .filter(r => r.status !== 'cancelled' && new Date(r.events?.start_date || r.events?.start) >= new Date())
    .sort((a, b) => new Date(a.events?.start_date || a.events?.start) - new Date(b.events?.start_date || b.events?.start));

  const pastRegistrations = registrations
    .filter(r => new Date(r.events?.end_date || r.events?.start_date || r.events?.start) < new Date())
    .sort((a, b) => new Date(b.events?.start_date || b.events?.start) - new Date(a.events?.start_date || a.events?.start));

  return {
    loading,
    error,
    profile,
    registrations,
    upcomingRegistrations,
    pastRegistrations,
    savedEvents,
    favoriteSpeakers,
    upcomingEvents,
    recommendedEvents,
    nextEvent,
    totalSpent,
    refresh: loadData,
  };
};

export default useAttendeeData;
